// Currency symbols — canonical display strings per currency code.
const CURRENCY_SYMBOLS: Record<string, string> = {
  KRW: '₩',
  USD: 'US$',
  SGD: 'S$',
  MYR: 'RM',
  IDR: 'Rp',
  BND: 'B$',
  EUR: '€',
  GBP: '£',
};

// Locale controls thousands/decimal separator style.
// KRW and IDR use 0 fraction digits — no decimal part rendered.
const CURRENCY_CONFIG: Record<string, { locale: string; fractionDigits: number }> = {
  KRW: { locale: 'ko-KR',  fractionDigits: 0 },
  USD: { locale: 'en-US',  fractionDigits: 2 },
  SGD: { locale: 'en-SG',  fractionDigits: 2 },
  MYR: { locale: 'ms-MY',  fractionDigits: 2 },
  IDR: { locale: 'id-ID',  fractionDigits: 0 },
  BND: { locale: 'en-US',  fractionDigits: 2 },
  EUR: { locale: 'de-DE',  fractionDigits: 2 },
  GBP: { locale: 'en-GB',  fractionDigits: 2 },
};

/**
 * Format a price into two segments:
 *  - `main`    : currency symbol + integer digits + thousands separators
 *  - `decimal` : decimal separator + fractional digits (null for KRW / IDR)
 *
 * Keeping them separate lets callers render the decimal in a distinct style.
 */
export function formatPriceParts(
  price: number,
  currency: string,
): { main: string; decimal: string | null } {
  const cfg = CURRENCY_CONFIG[currency] ?? CURRENCY_CONFIG.KRW;
  const symbol = CURRENCY_SYMBOLS[currency] ?? currency;
  const safePrice = Number.isFinite(price) ? price : 0;

  // Use style:'decimal' so we control the symbol ourselves.
  const formatter = new Intl.NumberFormat(cfg.locale, {
    style: 'decimal',
    minimumFractionDigits: cfg.fractionDigits,
    maximumFractionDigits: cfg.fractionDigits,
  });

  const parts = formatter.formatToParts(safePrice);
  const decimalIdx = parts.findIndex((p) => p.type === 'decimal');

  if (decimalIdx === -1) {
    return { main: symbol + parts.map((p) => p.value).join(''), decimal: null };
  }

  return {
    main: symbol + parts.slice(0, decimalIdx).map((p) => p.value).join(''),
    decimal: parts.slice(decimalIdx).map((p) => p.value).join(''),
  };
}

/** Plain formatted string (symbol + number) — for places that don't need styled decimal. */
export function formatPrice(price: number, currency: string): string {
  const { main, decimal } = formatPriceParts(price, currency);
  return decimal ? main + decimal : main;
}
